import { and, eq, getTableColumns, gte, isNotNull, isNull, or, sql } from "drizzle-orm";

import type { WsScheduleTrigger } from "@storyboard/web-client";
import { bulkInsert } from "@/db/bulkInsert";
import { db } from "@/db/db";
import { broadcastAction, scheduleTrigger, workflowItem } from "@/db/schema";
import { deduplicateByKeyValue } from "@/models/appSyncFunctions.ts";
import { emptyQuery } from "@/models/emptyQuery.ts";
import { emitTableUpdates } from "@/models/eventing/TableEventManager";
import { logQuery } from "@/models/logQuery.ts";

function transformWsScheduleTrigger(r: WsScheduleTrigger) {
  return {
    ...r,
    enabled: r?.enabled ? 1 : 0,
  };
}

export async function bulkUpsertScheduleTrigger(records: WsScheduleTrigger[]) {
  if (records.length === 0) return;
  const data = records.map((r) => transformWsScheduleTrigger(r));
  const deduped = deduplicateByKeyValue(data, (item) => item.id);
  await bulkInsert(scheduleTrigger, deduped, {
    onConflict: {
      type: "doUpdate",
      target: [scheduleTrigger.id],
      set: {
        oneTimeSchedule: sql`excluded."oneTimeSchedule"`,
        cronSchedule: sql`excluded."cronSchedule"`,
        workspaceId: sql`excluded."workspaceId"`,
        broadcastActionId: sql`excluded."broadcastActionId"`,
        createdAt: sql`excluded."createdAt"`,
        updatedAt: sql`excluded."updatedAt"`,
        timezone: sql`excluded."timezone"`,
        enabled: sql`excluded."enabled"`,
      },
    },
  });
  emitTableUpdates(["schedule_trigger"]);
}

export function scheduleTriggerSelect({ workspaceId, date }: { workspaceId?: string; date?: string }) {
  const query = db
    .selectDistinct({
      ...getTableColumns(scheduleTrigger),
    })
    .from(scheduleTrigger)
    .innerJoin(broadcastAction, eq(scheduleTrigger.broadcastActionId, broadcastAction.id))
    .innerJoin(workflowItem, eq(broadcastAction.workflowItemId, workflowItem.id))
    .where(
      and(
        eq(workflowItem.workspaceId, workspaceId),
        isNull(workflowItem.deletedAt),
        or(gte(scheduleTrigger.oneTimeSchedule, date), isNotNull(scheduleTrigger.cronSchedule)),
      ),
    );

  if (!workspaceId || !date) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Schedule Triggers", query);
  return query;
}
